import { useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { addDays, newId, type DepartFutureAssignments } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { roleLabel, type PersonView, type RoleRow } from '../../data/people.ts'
import {
  changeProblem, dayText, DEPART_WHAT, departProblem, hireProblem, nextChangeDay, nextFirstDay, plannedProblem, titleText,
  type JobTitleRow, type Period, type StaffEntry,
} from '../../data/staff.ts'
import { localDate } from '../../lib/time.ts'
import { Banner, Field, PendingBanner, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { StepUpField, useSensitive, type Sensitive } from '../../ui/sensitive.tsx'

/** A fonction to choose (the club's, not archived) and the access it gives, said under the list: the fonction is the club's word, the access role decides what the
 *  person sees and may do (M20 decision 1). */
function FonctionField({ id, titles, roles, value, onChange, exclude, testId }: {
  id: string; titles: readonly JobTitleRow[]; roles: readonly RoleRow[]; value: string; onChange: (v: string) => void; exclude?: string; testId: string
}) {
  const { t, i18n } = useTranslation()
  const chosen = titles.find((x) => x.id === value)
  return (
    <Field label={t('staff.hire.fonction')} htmlFor={id} hint={chosen ? t('staff.hire.access', { role: roleLabel(roles as RoleRow[], chosen.access_role_code, i18n.language) }) : undefined}>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        <option value="">{t('mounts.choose')}</option>
        {titles.filter((x) => !x.archived_at && x.id !== exclude).map((x) => <option key={x.id} value={x.id}>{titleText(x.label, i18n.language)}</option>)}
      </select>
    </Field>
  )
}

/** Who was just added (M23 item 52): the register offers the way to their access from it ("Donner un accès à <nom>"). */
export interface Hired { id: string; name: string }

/** "Ajouter un employé" (person.hire@1, Y*): a new person with their fonction and first day, or (`person` given) a person of the club hired again at this site. The
 *  first day is today by default, the planned last day a reminder only; the server checks everything again (the window, an overlap, an archived fonction).
 *  `onDone` carries the new person (a hire, not a re-hire) so the register can lead to their access. */
export function HireForm({ titles, roles, person, periods = [], s: shared, onDone }: {
  titles: readonly JobTitleRow[]; roles: readonly RoleRow[]; person?: { id: string; display_name: string }; periods?: readonly Period[]
  /** the section's own proof and messages, so the answer stays on screen once the form closes */ s?: Sensitive; onDone: (hired?: Hired) => void
}) {
  const { t } = useTranslation()
  const { site } = useClub()
  const own = useSensitive()
  const s = shared ?? own
  const today = localDate(new Date(), site.timezone)
  const id = useRef(newId())
  const [open, setOpen] = useState(Boolean(person))
  const [f, setF] = useState({ name: '', locale: 'fr' as 'fr' | 'en', jobTitleId: '', firstDay: nextFirstDay(periods, today), plannedLastDay: '' })
  const problem = hireProblem(f, today, !person)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const payload = {
      person_id: person?.id ?? id.current, job_title_id: f.jobTitleId, first_day: f.firstDay,
      ...(person ? {} : { display_name: f.name.trim(), preferred_locale: f.locale }), ...(f.plannedLastDay ? { planned_last_day: f.plannedLastDay } : {}),
    }
    void s.command('person.hire@1', payload, person ? 'staff.hire.againDone' : 'staff.hire.done').then((ok) => {
      if (!ok) return
      const hired: Hired | undefined = person ? undefined : { id: id.current, name: f.name.trim() }
      id.current = newId(); setF({ ...f, name: '', jobTitleId: '', plannedLastDay: '' }); onDone(hired)
    })
  }
  if (!open) return <button className="btn btn-primary self-start" onClick={() => setOpen(true)} data-testid="hire-open">{t('staff.hire.open')}</button>
  const p = person ? 're' : 'np'
  return (
    <form onSubmit={submit} className={`flex flex-col gap-3 ${person ? '' : 'card'}`} data-testid={person ? 'rehire' : 'person-new'}>
      {!person && <h2 className="text-lg font-bold">{t('staff.hire.title')}</h2>}
      {!person && <p className="meta">{t('staff.hire.help')}</p>}
      {!person && <Field label={t('people.name')} htmlFor="np-name"><input id="np-name" className="input" maxLength={120} required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="person-name" /></Field>}
      <FonctionField id={`${p}-job-${person?.id ?? ''}`} titles={titles} roles={roles} value={f.jobTitleId} onChange={(v) => setF({ ...f, jobTitleId: v })} testId="hire-job" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('staff.hire.firstDay')} htmlFor={`${p}-first-${person?.id ?? ''}`}>
          <input id={`${p}-first-${person?.id ?? ''}`} type="date" className="input" value={f.firstDay} onChange={(e) => setF({ ...f, firstDay: e.target.value })} data-testid="hire-first" />
        </Field>
        <Field label={t('staff.hire.planned')} hint={t('staff.hire.plannedHint')} htmlFor={`${p}-planned-${person?.id ?? ''}`}>
          <input id={`${p}-planned-${person?.id ?? ''}`} type="date" className="input" min={f.firstDay} value={f.plannedLastDay} onChange={(e) => setF({ ...f, plannedLastDay: e.target.value })} data-testid="hire-planned" />
        </Field>
      </div>
      {!person && (
        <Field label={t('people.language')} htmlFor="np-lang">
          <select id="np-lang" className="input" value={f.locale} onChange={(e) => setF({ ...f, locale: e.target.value as 'fr' | 'en' })} data-testid="hire-lang"><option value="fr">Français</option><option value="en">English</option></select>
        </Field>
      )}
      {problem && problem !== 'name' && problem !== 'fonction' && <p className="text-sm font-medium text-red-700 dark:text-red-300" role="alert" data-testid="hire-problem">{t(`staff.check.${problem}`)}</p>}
      <StepUpField s={s} />
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={s.busy || !s.online || problem !== null} data-testid={person ? 'rehire-go' : 'person-create'}>{person ? t('staff.hire.againGo') : t('staff.hire.go')}</button>
        {!person && <button type="button" className="btn" onClick={() => { setOpen(false); s.reset() }}>{t('common.cancel')}</button>}
      </div>
    </form>
  )
}

/** "Fonction et dates" of one person: the fonction and its days, the periods before, and for the direction the three changes: another fonction from a day, the planned
 *  last day (a reminder, an office intent), and "Enlever de l'équipe" — a departure, never a delete. The departure's answer lists the planned jobs after the last day,
 *  which the command never cancels, so the planner can give them to someone else. */
export function StaffJob({ person, entry, periods, titles, roles, canHire, canPlan, erased, onChanged }: {
  person: PersonView; entry: StaffEntry; periods: readonly Period[]; titles: readonly JobTitleRow[]; roles: readonly RoleRow[]
  /** member.manage: hire, change, depart (Y*) */ canHire: boolean
  /** person.manage: the planned last day */ canPlan: boolean
  erased: boolean; onChanged: () => void
}) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const today = localDate(new Date(), site.timezone)
  const s = useSensitive()
  const plan = useWorkshopAction()
  const [panel, setPanel] = useState<'change' | 'depart' | 'hire' | null>(null)
  const [future, setFuture] = useState<{ lastDay: string; answer: DepartFutureAssignments } | null>(null)
  const current = entry.period
  const openPeriod = current && current.last === null ? current : null
  // a departure recorded for today or a later day can still be brought forward (they leave sooner), never pushed back
  const leaving = current && current.last !== null && current.last >= today ? current : null
  const titleOf = (id: string) => titles.find((x) => x.id === id)
  const toggle = (p: typeof panel) => { s.reset(); setPanel(panel === p ? null : p) }
  const done = () => { setPanel(null); onChanged() }

  return (
    <div className="flex flex-col gap-3 border-t pt-4" data-testid="person-job">
      <h3 className="text-lg font-bold">{t('staff.job.title')}</h3>
      {current ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="job-current">
          <Pill tone="green">{titleText(titleOf(current.job_title_id)?.label, lang)}</Pill>
          <span className="text-sm">{t('staff.job.since', { day: dayText(current.first) })}</span>
          {current.last && <span className="text-sm font-semibold">{t('staff.job.lastDay', { day: dayText(current.last) })}</span>}
        </div>
      ) : <p className="meta">{t('staff.job.none')}</p>}
      {periods.length > 1 && (
        <div className="text-sm" data-testid="job-history">
          <p className="font-semibold">{t('staff.job.history')}</p>
          <ul className="list-disc pl-5">
            {periods.slice(0, -1).reverse().map((x) => <li key={x.id}>{titleText(titleOf(x.job_title_id)?.label, lang)} — {t('common.fromTo', { from: dayText(x.first), to: dayText(x.last ?? x.first) })}</li>)}
          </ul>
        </div>
      )}

      {/* the reminder of an open period: an office intent, not Y* (it changes no role) */}
      {openPeriod && canPlan && !erased && <PlannedLastDay key={`${openPeriod.id}:${openPeriod.planned_last_day ?? ''}`} personId={person.id} period={openPeriod} w={plan} />}

      {canHire && !erased && (
        <div className="flex flex-wrap gap-2">
          {openPeriod && <button className="btn" aria-expanded={panel === 'change'} onClick={() => toggle('change')} data-testid="job-change-open">{t('staff.job.change')}</button>}
          {!openPeriod && <button className="btn" aria-expanded={panel === 'hire'} onClick={() => toggle('hire')} data-testid="rehire-open">{current ? t('staff.hire.again') : t('staff.hire.existing')}</button>}
          {(openPeriod || leaving) && <button className="btn btn-danger" aria-expanded={panel === 'depart'} onClick={() => toggle('depart')} data-testid="depart-open">{t(leaving ? 'staff.depart.earlier' : 'staff.depart.open')}</button>}
        </div>
      )}
      {panel === 'change' && openPeriod && <ChangeJob personId={person.id} period={openPeriod} titles={titles} roles={roles} today={today} s={s} onDone={done} />}
      {panel === 'hire' && <HireForm titles={titles} roles={roles} person={person} periods={periods} s={s} onDone={done} />}
      {panel === 'depart' && (openPeriod ?? leaving) && (
        <Depart name={person.display_name} personId={person.id} period={(openPeriod ?? leaving)!} today={today} s={s} onCancel={() => setPanel(null)}
          onDone={(lastDay, answer) => { setFuture({ lastDay, answer }); done() }} />
      )}
      {!panel && s.ok && <Banner tone="ok"><span data-testid="sensitive-ok">{s.ok}</span></Banner>}
      {future && (
        <div data-testid="depart-result">
          <Banner tone={future.answer.count > 0 ? 'warn' : 'ok'}>
            {future.answer.count === 0 ? t('staff.depart.none') : (
              <>
                <span className="font-semibold">{t('staff.depart.future', { count: future.answer.count })}</span>
                <span className="mt-2 flex flex-wrap gap-2">
                  {future.answer.days.map((d) => (
                    <Link key={d} to="/plan" search={{ d } as never} className="btn !px-3" aria-label={t('staff.depart.openDay', { day: dayText(d) })} data-testid="depart-day-link" data-day={d}>{dayText(d)}</Link>
                  ))}
                </span>
              </>
            )}
          </Banner>
        </div>
      )}
    </div>
  )
}

function PlannedLastDay({ personId, period, w }: { personId: string; period: Period; w: ReturnType<typeof useWorkshopAction> }) {
  const { t } = useTranslation()
  const [day, setDay] = useState(period.planned_last_day ?? '')
  const problem = plannedProblem(day, period)
  const save = (v: string | null) => void w.run('person.set_planned_last_day@1', { person_id: personId, planned_last_day: v }, 'staff.job.plannedSaved')
  return (
    <div className="flex flex-col gap-2" data-testid="job-planned">
      <Field label={t('staff.job.planned')} hint={t('staff.job.plannedHint')} error={problem ? t(`staff.check.${problem}`) : null} htmlFor={`pl-${period.id}`}>
        <input id={`pl-${period.id}`} type="date" className="input" min={period.first} value={day} onChange={(e) => setDay(e.target.value)} data-testid="planned-day" />
      </Field>
      <div className="flex flex-wrap gap-2">
        <button className="btn" disabled={w.busy || !day || problem !== null || day === (period.planned_last_day ?? '')} onClick={() => save(day)} data-testid="planned-save">{t('staff.job.plannedSave')}</button>
        {period.planned_last_day && <button className="btn" disabled={w.busy} onClick={() => save(null)} data-testid="planned-clear">{t('staff.job.plannedClear')}</button>}
      </div>
      {w.ok && <Banner tone="ok">{w.ok}</Banner>}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
    </div>
  )
}

function ChangeJob({ personId, period, titles, roles, today, s, onDone }: {
  personId: string; period: Period; titles: readonly JobTitleRow[]; roles: readonly RoleRow[]; today: string; s: Sensitive; onDone: () => void
}) {
  const { t } = useTranslation()
  // the period's planned last day carries over to the next one (the server stores only what is sent: an empty field would drop it), unless it falls before the change
  const fromDay = nextChangeDay(period, today)
  const [f, setF] = useState({ jobTitleId: '', fromDay, plannedLastDay: period.planned_last_day && period.planned_last_day >= fromDay ? period.planned_last_day : '' })
  const problem = changeProblem(f, period, today)
  return (
    <form className="card flex flex-col gap-3" data-testid="job-change" onSubmit={(e) => {
      e.preventDefault()
      void s.command('person.change_job@1', { person_id: personId, job_title_id: f.jobTitleId, from_day: f.fromDay, ...(f.plannedLastDay ? { planned_last_day: f.plannedLastDay } : {}) }, 'staff.job.changed')
        .then((ok) => { if (ok) onDone() })
    }}>
      <p className="meta">{t('staff.job.changeHelp')}</p>
      <FonctionField id={`cj-${personId}`} titles={titles} roles={roles} value={f.jobTitleId} onChange={(v) => setF({ ...f, jobTitleId: v })} exclude={period.job_title_id} testId="change-job" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('staff.job.changeFrom')} htmlFor={`cf-${personId}`}><input id={`cf-${personId}`} type="date" className="input" value={f.fromDay} onChange={(e) => setF({ ...f, fromDay: e.target.value })} data-testid="change-from" /></Field>
        <Field label={t('staff.hire.planned')} hint={t('staff.hire.plannedHint')} htmlFor={`cp-${personId}`}><input id={`cp-${personId}`} type="date" className="input" min={f.fromDay} value={f.plannedLastDay} onChange={(e) => setF({ ...f, plannedLastDay: e.target.value })} data-testid="change-planned" /></Field>
      </div>
      {problem && problem !== 'fonction' && <p className="text-sm font-medium text-red-700 dark:text-red-300" role="alert">{t(`staff.check.${problem}`)}</p>}
      <StepUpField s={s} />
      <button className="btn btn-primary self-start" disabled={s.busy || !s.online || problem !== null} data-testid="change-go">{t('staff.job.changeGo')}</button>
    </form>
  )
}

/** The departure's confirmation: what happens (access ends after the last day, history kept, planned jobs listed for reassignment, the sheet's retention) and that the
 *  erasure is a separate step. */
function Depart({ name, personId, period, today, s, onCancel, onDone }: {
  name: string; personId: string; period: Period; today: string; s: Sensitive; onCancel: () => void; onDone: (lastDay: string, answer: DepartFutureAssignments) => void
}) {
  const { t } = useTranslation()
  // bringing a recorded departure forward starts from today (or yesterday, when the recorded last day is today)
  const [lastDay, setLastDay] = useState(period.last ? (period.last > today ? today : addDays(today, -1)) : period.first > today ? period.first : today)
  const problem = departProblem(lastDay, period, today)
  return (
    <form className="card flex flex-col gap-3 border-2 border-red-400 dark:border-red-500" data-testid="depart-confirm" onSubmit={(e) => {
      e.preventDefault()
      void s.commandResult('person.depart@1', { person_id: personId, last_day: lastDay }, 'staff.depart.done').then((r) => {
        if (!r) return
        const answer = (r.future_assignments as DepartFutureAssignments | undefined) ?? { count: 0, days: [] }
        onDone(lastDay, answer)
      })
    }}>
      <h4 className="text-lg font-bold">{t('staff.depart.title', { name })}</h4>
      <Field label={t('staff.depart.lastDay')} error={problem ? t(`staff.check.${problem}`) : null} htmlFor={`dp-${personId}`}>
        <input id={`dp-${personId}`} type="date" className="input" min={period.first} max={period.last ? addDays(period.last, -1) : undefined} value={lastDay} onChange={(e) => setLastDay(e.target.value)} data-testid="depart-day" />
      </Field>
      <ul className="list-disc pl-5 text-sm" data-testid="depart-what">{DEPART_WHAT.map((k) => <li key={k}>{t(`staff.depart.what.${k}`)}</li>)}</ul>
      <Banner tone="info">{t('staff.depart.erase')}</Banner>
      <StepUpField s={s} />
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-danger" disabled={s.busy || !s.online || problem !== null} data-testid="depart-go">{t('staff.depart.go')}</button>
        <button type="button" className="btn" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}
